import { useState } from "react";
import { getAllMovies, type FilterOptions } from "@/utils/movies";
import { typeOptions, regionOptions, yearOptions, sortOptions } from "@/mocks/movies";

interface FilterBarProps {
  value: Required<Omit<FilterOptions, "sort">> & { sort: string };
  onChange: (patch: Partial<FilterOptions>) => void;
  onReset: () => void;
  resultCount: number;
}

const allGenres = Array.from(new Set(getAllMovies().flatMap((movie) => movie.genres)));

function FilterGroup({
  label,
  options,
  active,
  onSelect,
}: {
  label: string;
  options: string[];
  active: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
              active === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-200/70 text-foreground-300 hover:bg-background-300"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ value, onChange, onReset, resultCount }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const groups = (
    <>
      <FilterGroup
        label="类型"
        options={typeOptions}
        active={value.type}
        onSelect={(type) => onChange({ type })}
      />
      <FilterGroup
        label="地区"
        options={regionOptions}
        active={value.region}
        onSelect={(region) => onChange({ region })}
      />
      <FilterGroup
        label="年份"
        options={yearOptions}
        active={value.year}
        onSelect={(year) => onChange({ year })}
      />
      <FilterGroup
        label="题材标签"
        options={["全部", ...allGenres]}
        active={value.genre}
        onSelect={(genre) => onChange({ genre })}
      />
    </>
  );

  return (
    <div className="bg-background-100 border border-background-200/60 rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-foreground-300">
            共 <span className="text-accent-500 font-semibold">{resultCount}</span> 部作品
          </span>
          <span className="hidden md:inline text-xs text-foreground-600">支持多条件组合筛选</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-xs md:text-sm text-foreground-400 hover:text-foreground-50 px-3 h-9 rounded-md border border-background-300/60 whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden inline-flex items-center gap-1 text-xs text-background-50 bg-primary-500 px-3 h-9 rounded-md whitespace-nowrap cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>
        </div>
      </div>

      <div className="hidden md:flex flex-col gap-5">
        {groups}
        <div className="flex flex-col gap-3 pt-5 border-t border-background-200/60">
          <span className="text-xs text-foreground-500">排序方式</span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onChange({ sort: option.key })}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  value.sort === option.key
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-200/70 text-foreground-300 hover:bg-background-300"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[70] bg-background-50 flex flex-col hn-fade">
          <div className="flex items-center justify-between px-4 h-16 border-b border-background-200/60">
            <span className="font-heading font-bold text-foreground-50">筛选条件</span>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setMobileOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-300 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-6">
            {groups}
            <div className="flex flex-col gap-3">
              <span className="text-xs text-foreground-500">排序方式</span>
              <div className="flex flex-wrap gap-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => onChange({ sort: option.key })}
                    className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      value.sort === option.key
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-200/70 text-foreground-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-background-200/60 flex items-center gap-3">
            <button
              type="button"
              onClick={onReset}
              className="flex-1 h-11 rounded-md border border-background-300/60 text-foreground-200 text-sm whitespace-nowrap cursor-pointer"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
            >
              查看 {resultCount} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}